import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { useLeads } from "@/hooks/useLeads";
import { Flame, MapPin, Clock, DollarSign, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import StatusUpdateSheet from "@/components/StatusUpdateSheet";
import type { DbLead } from "@/lib/types";

function getScoreTier(score: number) {
  if (score >= 85) return { label: score === 100 ? "Emergency" : "Hot", border: "border-l-hot", pill: "bg-hot/10 text-hot" };
  if (score >= 50) return { label: "Warm", border: "border-l-warning", pill: "bg-warning/10 text-warning" };
  return { label: "Cold", border: "border-l-muted-foreground", pill: "bg-muted text-muted-foreground" };
}

function relativeTime(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / 3600_000);
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const FILTERS = ["All", "New", "In Progress", "Follow Up", "Booked", "Completed"] as const;

export default function StaffTasks() {
  const { profile } = useProfile();
  const { leads: myLeads, loading, refetch } = useLeads({ assignedToMe: true });
  const navigate = useNavigate();
  const [filter, setFilter] = useState<typeof FILTERS[number]>("All");
  const [statusLead, setStatusLead] = useState<DbLead | null>(null);

  const seg = (profile as any)?.segment ?? "homecare";
  const firstName = (profile?.owner_name ?? "").split(" ")[0] || "there";

  const counts = useMemo(() => ({
    new: myLeads.filter(l => l.status === "new").length,
    contacted: myLeads.filter(l => l.status === "contacted" || l.status === "voicemail").length,
    booked: myLeads.filter(l => l.status === "booked").length,
  }), [myLeads]);

  const filtered = useMemo(() => {
    if (filter === "All") return myLeads;
    if (filter === "New") return myLeads.filter(l => l.status === "new");
    // Voicemail is still an active-contact-attempt state, so it groups with
    // "In Progress"; "Follow Up" is its own explicit status a staffer sets
    // when a lead needs a deliberate follow-up touch.
    if (filter === "In Progress") return myLeads.filter(l => l.status === "contacted" || l.status === "voicemail");
    if (filter === "Follow Up") return myLeads.filter(l => l.status === "follow_up");
    if (filter === "Booked") return myLeads.filter(l => l.status === "booked");
    if (filter === "Completed") return myLeads.filter(l => l.status === "closed");
    return myLeads;
  }, [myLeads, filter]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gold border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-20 md:pb-8">
      {/* Greeting */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy">{greeting}, {firstName}.</h1>
        <p className="text-base text-muted-foreground mt-1">
          {seg === "grouphome"
            ? "Here are the placement inquiries assigned to you today."
            : "Here are the leads assigned to you today."}
        </p>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setFilter("New")}
          className={`px-4 py-2 rounded-full bg-gold/15 text-navy transition-opacity hover:opacity-80 ${filter === "New" ? "ring-2 ring-gold" : ""}`}
        >
          <span className="font-bold">{counts.new}</span>
          <span className="text-sm ml-1.5">New</span>
        </button>
        <button
          type="button"
          onClick={() => setFilter("In Progress")}
          className={`px-4 py-2 rounded-full bg-primary/15 text-primary transition-opacity hover:opacity-80 ${filter === "In Progress" ? "ring-2 ring-primary" : ""}`}
        >
          <span className="font-bold">{counts.contacted}</span>
          <span className="text-sm ml-1.5">In Progress</span>
        </button>
        <button
          type="button"
          onClick={() => setFilter("Booked")}
          className={`px-4 py-2 rounded-full bg-success/15 text-success transition-opacity hover:opacity-80 ${filter === "Booked" ? "ring-2 ring-success" : ""}`}
        >
          <span className="font-bold">{counts.booked}</span>
          <span className="text-sm ml-1.5">Booked</span>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f ? "bg-navy text-navy-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-muted bg-card py-16 px-6 text-center">
          <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-base text-foreground font-medium">You're all caught up.</p>
          <p className="text-sm text-muted-foreground mt-1">
            {filter === "All"
              ? "Your manager will assign new leads when inquiries come in."
              : `No leads in this filter.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(lead => {
            const tier = getScoreTier(lead.score);
            return (
              <article
                key={lead.id}
                className={`bg-card rounded-2xl shadow-sm border-l-4 ${tier.border} border-r border-y border-border p-5 min-h-[160px]`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-[17px] font-bold text-navy flex items-center gap-1.5">
                    {lead.score === 100 && <Flame className="h-4 w-4 text-gold animate-pulse" />}
                    {lead.name}
                  </h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${tier.pill}`}>
                    {lead.score}
                  </span>
                </div>

                <p className="text-[15px] text-muted-foreground mb-3">
                  {(lead.acuity_notes ?? "—").slice(0, 80)}
                  {(lead.acuity_notes?.length ?? 0) > 80 ? "..." : ""}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {lead.zip_code ?? "—"}
                  </span>
                  {lead.urgency != null && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      lead.urgency >= 4 ? "bg-hot/10 text-hot" : lead.urgency >= 3 ? "bg-warning/10 text-warning" : "bg-muted text-muted-foreground"
                    }`}>
                      <Clock className="h-3 w-3" /> Urgency {lead.urgency}/5
                    </span>
                  )}
                  {lead.payer_source && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                      <DollarSign className="h-3 w-3" /> {lead.payer_source}
                    </span>
                  )}
                </div>

                <p className="text-[13px] italic text-muted-foreground mb-4">
                  Assigned · {relativeTime(lead.created_at)}
                </p>

                <div className="border-t pt-3 flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => setStatusLead(lead)}
                    className="bg-gold text-gold-foreground hover:bg-gold/90 font-bold min-h-[44px] w-full sm:w-auto sm:flex-1"
                  >
                    Update Status
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/leads`)}
                    className="border-navy text-navy hover:bg-navy/5 min-h-[44px] w-full sm:w-auto"
                  >
                    View Details
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <StatusUpdateSheet
        lead={statusLead}
        onClose={() => setStatusLead(null)}
        onSaved={refetch}
      />
    </div>
  );
}
